import { Ioption } from '../../models';

/*
 * Pure helpers for the MultiSelectFilterDropdown and the install-base room filter.
 * Kept free of React/UI imports so they can be unit-tested in isolation.
 */

export type IsimpleOption = Pick<Ioption, 'value' | 'label'>;

/**
 * Case-insensitive label filter for the searchable option list.
 */
export const filterOptionsBySearch = <T extends IsimpleOption>(options: T[], search: string): T[] => {
    const term = search.trim().toLowerCase();
    if (!term) {
        return options;
    }
    return options.filter(option => option.label.toLowerCase().includes(term));
};

/**
 * Toggle a single option's membership in the current draft selection.
 */
export const toggleOption = (draft: IsimpleOption[], option: IsimpleOption): IsimpleOption[] => {
    const exists = draft.some(item => item.value === option.value);
    if (exists) {
        return draft.filter(item => item.value !== option.value);
    }
    return [...draft, { value: option.value, label: option.label }];
};

/**
 * True when every currently-visible option is already in the draft (and there is
 * at least one visible option). Drives the "Select All" checkbox checked state.
 */
export const areAllVisibleSelected = (draft: IsimpleOption[], visibleOptions: IsimpleOption[]): boolean => {
    if (!visibleOptions.length) {
        return false;
    }
    return visibleOptions.every(option => draft.some(item => item.value === option.value));
};

/**
 * Select-all / clear-all scoped to the currently-visible (search-filtered) options.
 * If all visible are already selected, they are removed; otherwise all visible are added.
 * Selections outside the visible set are preserved either way.
 */
export const toggleSelectAllVisible = (
    draft: IsimpleOption[],
    visibleOptions: IsimpleOption[]
): IsimpleOption[] => {
    if (!visibleOptions.length) {
        return draft;
    }
    if (areAllVisibleSelected(draft, visibleOptions)) {
        const visibleValues = new Set(visibleOptions.map(option => option.value));
        return draft.filter(item => !visibleValues.has(item.value));
    }
    const draftValues = new Set(draft.map(item => item.value));
    const additions = visibleOptions
        .filter(option => !draftValues.has(option.value))
        .map(option => ({ value: option.value, label: option.label }));
    return [...draft, ...additions];
};

/**
 * Install-base room-filter predicate shared with the manage-inventory selector.
 * Empty/undefined selection means "no room filter" (include everything).
 */
export const roomMatchesSelectedFilter = (
    selectedRooms: IsimpleOption[] | undefined,
    roomID: string | undefined
): boolean => {
    if (!selectedRooms || !selectedRooms.length) {
        return true;
    }
    return selectedRooms.some(room => room.value === roomID);
};

/*
 * Ascending order for building / floor / location / room labels.
 *
 * `localeCompare` on its own is lexicographic, so a list of rooms comes out 1, 10, 2 - the ordering
 * the ticket complains about. `numeric: true` compares digit runs by value instead, which handles
 * all three shapes a label can take (bare number, number + words, words) in one pass:
 *
 *   1 · A Room · Room 1 · Room 2 · Room 10 · The Room
 */
const naturalCollator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });

export const compareLabelsNatural = (a: string, b: string): number => naturalCollator.compare(a || '', b || '');

/** Non-mutating natural sort for an option list. */
export const sortOptionsNatural = <T extends IsimpleOption>(options: T[]): T[] =>
    [...options].sort((a, b) => compareLabelsNatural(a.label, b.label));
